import { createSelector } from 'reselect';
import type { RootState } from 'typesafe-actions';

import type { MarketType } from '@travel/i18n';
import { getMarket } from '@travel/i18n';
import type { ItemPlanGuest } from '@travel/traveler-core/types/bookingStep';

import { getCountries } from 'store/countries/selectors';
import { getItem as getMemberData } from 'store/member/selectors';
import { getItems } from 'store/reservationDetails/selectors';

import type {
  BookingForm,
  BookingStep3,
  BookingStepData,
  BookingStepErrors,
  SalesPromotion,
} from 'BookingStep-Types';
import { NAME_INPUT_REGEX } from 'constants/bookingStep1Form';
import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import type { CountriesItems } from 'Countries-Types';
import type { MemberItem } from 'Member-Types';
import type { ReservationDetailsItems } from 'ReservationDetails-Types';

import type { BookingStepState } from './reducer';

const DEFAULT_LAST_CHECK_IN_TIME = '24:00';

export const getBooking = (state: RootState) => state.bookingStep;

export const getErrors = (state: RootState) => state.bookingStep.errors;

export const getBookingStepData = (state: RootState) => state.bookingStep.bookingData;

export const getBookingStep3 = (state: RootState) => state.bookingStep.step3;

export const getCouponRequest = (state: RootState) => state.bookingStep.couponRequestBody;

export const getChildNum = (state: RootState) => state.bookingStep.bookingData.childAges;

export const getBinRestrictedCouponId = (state: RootState) =>
  state.bookingStep.bookingData.appliedBinRestrictedCouponId;

export const getBookingForm = createSelector(
  getBooking,
  getMemberData,
  getMarket,
  getCountries,
  (
    booking: BookingStepState,
    member: MemberItem,
    market: MarketType,
    countries: CountriesItems,
  ) => {
    const { bookingForm, bookingData } = booking;

    const { noOfAdults, childAges, roomCount } = bookingData;
    const noOfGuest = noOfAdults + (childAges ? childAges.length : 0);

    // searches the alpha2Code of the country based on the member's mobilePhoneCountryCallingCode
    const memberCountryCodeWithoutPlus = member.mobilePhoneCountryCallingCode?.replace('+', '');
    const memberCountryCode = Object.keys(countries).find(
      (key) => countries[key].callingCode === memberCountryCodeWithoutPlus,
    );

    // order of priority:
    // 1. existing booking step data saved in state
    // 2. logged-in user has data in myRakuten account
    // 3. fetch data from current market
    // 4. default (empty string)

    const countryCode =
      bookingForm.applicant?.countryCode ||
      (memberCountryCode && countries[memberCountryCode]?.alpha2Code) ||
      countries[market.marketCode]?.alpha2Code ||
      '';
    const phoneCountryCode =
      bookingForm.applicant?.phoneCountryCode ||
      member.mobilePhoneCountryCallingCode ||
      countries[market.marketCode]?.callingCode ||
      '';

    // non-member
    if (Object.keys(member).length === 0) {
      let itemPlanZeroGuestZero: ItemPlanGuest = {
        firstName: '',
        lastName: '',
        countryOfResidence: '',
        age: null,
        guestSequence: 0,
        representative: true,
      };
      if (bookingForm.isBookingForDifferentGuest && bookingForm.itemPlans[0].guests[0]) {
        itemPlanZeroGuestZero = { ...bookingForm.itemPlans[0].guests[0] };
      } else {
        if (bookingForm.applicant.firstName) {
          itemPlanZeroGuestZero.firstName = bookingForm.applicant.firstName;
        }
        if (bookingForm.applicant.lastName) {
          itemPlanZeroGuestZero.lastName = bookingForm.applicant.lastName;
        }
        if (bookingForm.applicant.countryOfResidence) {
          itemPlanZeroGuestZero.countryOfResidence = bookingForm.applicant.countryOfResidence;
        }
      }

      const itemPlans = bookingForm.itemPlans.map((itemPlan, itemPlanIndex) => {
        const guests = Array(noOfGuest)
          .fill(undefined)
          .map((_, guestIndex) => {
            if (itemPlanIndex === 0 && guestIndex === 0) {
              return {
                ...itemPlanZeroGuestZero,
                guestSequence: guestIndex + 1,
              };
            }
            return itemPlan.guests[guestIndex]
              ? itemPlan.guests[guestIndex]
              : {
                  firstName: '',
                  lastName: '',
                  representative: false,
                  guestSequence: guestIndex + 1,
                  age:
                    guestIndex >= noOfAdults && childAges
                      ? childAges[guestIndex - noOfAdults]
                      : null,
                  countryOfResidence: '',
                };
          });
        // null for adult (representative is always adult)
        guests[0].age = null;
        return {
          ...itemPlan,
          guests,
        };
      });

      return {
        ...bookingForm,
        applicant: {
          ...bookingForm.applicant,
          email: bookingData.nonMemberApplicantEmail || bookingForm.applicant.email,
          countryCode,
          phoneCountryCode:
            `${phoneCountryCode}`.substring(0, 1) === '+'
              ? phoneCountryCode
              : `+${phoneCountryCode}`,
        },
        itemPlans,
      };
    }

    // member
    const applicantEmail = bookingForm.nonMemberApplicantEmail
      ? bookingForm.nonMemberApplicantEmail
      : member.email;
    const applicantFirstName =
      bookingForm?.applicant?.firstName ||
      (NAME_INPUT_REGEX.test(member.firstName) && member.firstName) ||
      '';
    const applicantLastName =
      bookingForm?.applicant?.lastName ||
      (NAME_INPUT_REGEX.test(member.lastName) && member.lastName) ||
      '';
    const applicantCountryOfResidence = bookingForm?.applicant?.countryOfResidence || '';
    const phoneNumber = bookingForm.applicant?.phoneNumber || member.mobilePhoneNumber || '';

    const itemPlans = bookingForm.itemPlans.map((itemPlan, itemPlanIndex) => {
      const guests = Array(noOfGuest)
        .fill(undefined)
        .map((_, guestIndex) => {
          return itemPlan.guests[guestIndex] &&
            itemPlan.guests[guestIndex].firstName !== '' &&
            itemPlan.guests[guestIndex].lastName !== ''
            ? itemPlan.guests[guestIndex]
            : {
                // auto-filling the name of the representative of the first room
                firstName:
                  guestIndex === 0 && itemPlanIndex === 0 && applicantFirstName
                    ? applicantFirstName
                    : '',
                lastName:
                  guestIndex === 0 && itemPlanIndex === 0 && applicantLastName
                    ? applicantLastName
                    : '',
                countryOfResidence:
                  guestIndex === 0 && itemPlanIndex === 0 && applicantCountryOfResidence
                    ? applicantCountryOfResidence
                    : '',
                representative: guestIndex === 0,
                guestSequence: guestIndex + 1,
                age:
                  guestIndex >= noOfAdults && childAges ? childAges[guestIndex - noOfAdults] : null,
              };
        });
      guests[0].age = null;
      return {
        ...itemPlan,
        guests,
      };
    });

    return {
      ...bookingForm,
      roomCount,
      applicant: {
        firstName: applicantFirstName,
        lastName: applicantLastName,
        email: applicantEmail,
        countryCode,
        membership: 'RAKUTEN',
        phoneCountryCode:
          `${phoneCountryCode}`.substring(0, 1) === '+' ? phoneCountryCode : `+${phoneCountryCode}`,
        phoneNumber,
        countryOfResidence: applicantCountryOfResidence,
      },
      itemPlans,
    };
  },
);

export const getContractType = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.contractType,
);

export const getPaymentMethodForIndirectProvider = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    if (state.contractType === PROVIDER_CONTRACT_TYPES.DIRECT) {
      return undefined;
    }
    if (state.pricingDetails) {
      const hasLocal = state.pricingDetails[0].paymentMethods.includes('LOCAL');
      return hasLocal ? 'LOCAL' : 'CREDIT_CARD';
    }
    return 'LOCAL';
  },
);

export const getArrivalTime = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.expectedArrivalTime,
);

export const getProviderNotices = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.providerNotices,
);

export const getProviderQuestions = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.providerQuestions,
);

export const getProviderCheckInTime = createSelector(
  getBookingStepData,
  (state: BookingStepData) => ({
    startCheckInTime: state.providerCheckInTime,
    endCheckInTime: state.providerLastCheckInTime
      ? state.providerLastCheckInTime
      : DEFAULT_LAST_CHECK_IN_TIME,
  }),
);

export const getPricingDetails = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.pricingDetails,
);

export const getIsRefundable = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.isRefundable,
);

export const getSupplierType = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.supplierType,
);

export const getAllowPointUsage = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.allowPointUsage,
);

export const getUserCurrency = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.userCurrency,
);

export const getSavedCards = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.savedCards,
);

export const getAvailablePaymentMethods = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.availablePaymentMethods,
);

export const getTotalEarnedPoints = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.totalEarnedPoints,
);

export const getTaxInformation = createSelector(getBookingStepData, (state: BookingStepData) => {
  const { includeTax, excludeTax } = state;

  return {
    inclusive: includeTax,
    exclusive: excludeTax,
  };
});

export const getProviderCurrency = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.providerCurrency,
);

export const getBookingStep1Total = createSelector(
  getBookingStepData,
  (state: BookingStepData) => ({
    providerCurrencyTotal: state.totalPriceInProviderCurrency,
    userCurrencyTotal: state.totalPriceInUserCurrency,
    afterDiscountTotal: state.totalPriceUserAfterDiscount,
    userCurrency: state.userCurrency,
    providerCurrency: state.providerCurrency,
    earnPoint: state.totalEarnedPoints,
  }),
);

export const getFeatureGroups = createSelector(getBookingStepData, (state: BookingStepData) => {
  const { item, ratePlan, provider } = state;
  return {
    itemFeatureGroups: item.featureGroups,
    ratePlanFeatureGroups: ratePlan.featureGroups,
    providerFeatureGroups: provider.featureGroups,
  };
});

export const getRoomInformation = createSelector(getBookingStepData, (state: BookingStepData) => {
  const {
    itemName,
    itemNameGoogleTranslated,
    bedSizes,
    capacity,
    roomSize,
    itemVisualContent,
    visualContent,
    ratePlanName,
    ratePlanDescription,
    itemView,
    isRatePlanTextsGoogleTranslated,
  } = state;

  return {
    itemName,
    isItemNameGoogleTranslated: itemNameGoogleTranslated,
    bedSizes,
    capacity,
    roomSize,
    itemVisualContent,
    visualContent,
    ratePlanName,
    ratePlanDescription,
    itemView: itemView?.text,
    isRatePlanTextsGoogleTranslated,
  };
});

export const getReservationDetails = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    const {
      checkInDate,
      checkOutDate,
      providerName,
      roomCount,
      providerCheckInTime,
      providerCheckOutTime,
      providerLastCheckInTime,
      noOfAdults,
      childAges,
      totalPriceInProviderCurrency,
    } = state;

    return {
      checkInDate,
      checkOutDate,
      providerName,
      roomCount,
      providerCheckInTime,
      providerCheckOutTime,
      providerLastCheckInTime,
      noOfAdults,
      childrenAges: childAges,
      noOfChildren: childAges?.length,
      totalPriceInProviderCurrency,
      lengthOfStay:
        (new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) / 1000 / 86400,
      noOfGuests: noOfAdults + (childAges?.length || 0),
    };
  },
);

export const getCancelPolicyDetail = createSelector(
  getBookingStepData,
  (step1: BookingStepData) => step1.cancelPolicyDetails,
);

export const getPointUnitOfUsage = createSelector(
  getBookingStepData,
  (step1: BookingStepData) => step1.pointUnitOfUsage,
);

export const getBookingFormApplicant = createSelector(
  getBookingForm,
  (state: BookingForm) => state.applicant,
);

export const getBookingStepSalesPromotion = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    // in multi-room booking, remove which promotion have useMax === 1 and already has been selected by one room.
    let useMaxItemId = '';
    state?.pricingDetails?.map((pricingDetail) =>
      pricingDetail.salesPromotions.map((item) => {
        if (item.detail.maxUse === 1 && item.selected) {
          useMaxItemId = item.detail.id;
        }
        return item;
      }),
    );

    if (useMaxItemId) {
      state?.pricingDetails?.map(
        (pricingDetail) =>
          (pricingDetail.salesPromotions = pricingDetail.salesPromotions.filter((item) => {
            return item.detail.id !== useMaxItemId || item.selected;
          })),
      );
    }

    const salePromotion = state?.pricingDetails?.reduce(
      (salesPromotionData, pricingDetail) => {
        salesPromotionData[pricingDetail.echoId] = pricingDetail.salesPromotions;
        return salesPromotionData;
      },
      {} as { [key: string]: SalesPromotion[] },
    );

    return salePromotion || {};
  },
);

export const getBookingStep2Id = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.stepId,
);

export const getApplicantEmail = createSelector(
  getBookingStep3,
  getItems,
  (bookingStep3Data: BookingStep3, reservationDetails: ReservationDetailsItems) =>
    bookingStep3Data.applicantEmail || reservationDetails.email,
);

export const getMemberInformation = createSelector(
  getBookingStepData,
  (bookingData: BookingStepData) => bookingData.memberInformation,
);

export const getIsFetching = createSelector(
  getBooking,
  (state: BookingStepState) => state.isFetching,
);

export const getIsFetchingStep1 = createSelector(
  getBooking,
  (state: BookingStepState) => state.isFetchingStep1,
);

export const getIsFetchingStep3 = createSelector(
  getBooking,
  (state: BookingStepState) => state.isFetchingStep3,
);

export const getItemId = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.pricingDetails?.[0]?.itemId,
);

export const getRatePlanId = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.pricingDetails?.[0]?.itemRatePlanId,
);

export const getNonMemberApplicantEmail = createSelector(
  getBookingStepData,
  (state: BookingStepData) => state.nonMemberApplicantEmail,
);

export const getPaymentData = createSelector(getBookingStepData, (state: BookingStepData) => ({
  selectedPaymentMethod: state.selectedPaymentMethod,
}));

export const getPaymentAmountInJpy = createSelector(
  getBookingStep3,
  (state: BookingStep3) => state.paymentAmountInJpy,
);

export const getAppliedCoupon = createSelector(
  getBooking,
  (state: BookingStepState) => state.appliedCoupon,
);

export const getCheckoutStage = createSelector(
  getBooking,
  (state: BookingStepState) => state.checkoutStage,
);

export const getError = createSelector(getErrors, (state: BookingStepErrors) => {
  if (state?.errors) {
    return state.errors.length > 0 ? state.errors?.[0] : null;
  } else {
    return null;
  }
});

export const getErrorStatusCode = createSelector(getErrors, (state: BookingStepErrors) => {
  return state?.status;
});

export const getSelectedSalePromotions = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    return (
      state?.pricingDetails?.reduce((curr: Array<string | undefined>, pricingDetail) => {
        const selectedPromotions = pricingDetail.salesPromotions.filter(
          (salesPromotion) => salesPromotion.selected,
        );

        const mainIds = selectedPromotions.map((salesPromotion) => salesPromotion.detail.id);
        const subIds = pricingDetail.salesPromotions
          .map((salesPromotion) => salesPromotion?.subPromotions)
          .flat()
          .filter((subSalesPromotion) => subSalesPromotion?.selected)
          .map((subSalesPromotion) => subSalesPromotion?.detail.id);

        return [...curr, ...mainIds, ...subIds].filter((id) => id);
      }, []) || []
    );
  },
);

export const getProviderFacilities = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    const { featureGroups } = state;

    return featureGroups
      ?.filter((featureGroup) => featureGroup.type === 'Facility')[0]
      ?.features.map((feature) => feature.name)
      .filter((facility) => facility);
  },
);

export const getDestinationCountry = createSelector(
  getBookingStepData,
  (state: BookingStepData) => {
    return state.areaInfo?.country;
  },
);
